import { z } from 'zod';
import { TIME_CLOCK_STATUS_VALUES, WEBAUTHN_CREDENTIAL_STATUS_VALUES } from '@fas/types';
import { nonEmptyTrimmed, paginationQuerySchema, uuidSchema } from './common';

// ── Biometrics (Phase 3H, ADR-063) ─────────────────────────────────────────────
/** Binary WebAuthn fields travel as base64url without padding. */
const base64url = (max: number) => z.string().min(1).max(max).regex(/^[A-Za-z0-9_-]+$/, 'Must be base64url');

/** What `navigator.credentials.create()` produced, with its binary fields in base64url. */
export const webauthnRegistrationSchema = z
  .object({
    label: nonEmptyTrimmed(100, 'Name'),
    credential: z
      .object({
        id: base64url(1400),
        clientDataJSON: base64url(4000),
        attestationObject: base64url(16000),
        transports: z.array(z.string().max(20)).max(10).optional(),
      })
      .strict(),
  })
  .strict();
export type WebauthnRegistrationInput = z.infer<typeof webauthnRegistrationSchema>;

/** What `navigator.credentials.get()` produced for a clock event. */
export const webauthnAssertionSchema = z
  .object({
    id: base64url(1400),
    clientDataJSON: base64url(4000),
    authenticatorData: base64url(2000),
    signature: base64url(2000),
    userHandle: base64url(200).nullable().optional(),
  })
  .strict();
export type WebauthnAssertionInput = z.infer<typeof webauthnAssertionSchema>;

/** A challenge for verifying the user before a clock event, bound to its request id. */
export const webauthnAssertionOptionsSchema = z
  .object({
    purpose: z.enum(['CLOCK_IN', 'CLOCK_OUT'] as const),
    requestId: uuidSchema,
  })
  .strict();
export type WebauthnAssertionOptionsInput = z.infer<typeof webauthnAssertionOptionsSchema>;

export const revokeWebauthnCredentialSchema = z.object({ reason: nonEmptyTrimmed(500, 'Reason') }).strict();
export type RevokeWebauthnCredentialInput = z.infer<typeof revokeWebauthnCredentialSchema>;

export const listWebauthnCredentialsQuerySchema = paginationQuerySchema.extend({
  status: z.enum(WEBAUTHN_CREDENTIAL_STATUS_VALUES).optional(),
  userId: uuidSchema.optional(),
});
export type ListWebauthnCredentialsQuery = z.infer<typeof listWebauthnCredentialsQuerySchema>;

/** A terminal's identifier as its software reports it (serial number or device id). */
const terminalId = z.string('Enter the time clock’s identifier').trim().min(1, 'Enter the time clock’s identifier').max(100).regex(/^[\w.:/-]+$/, 'Letters, digits and . _ : / - only');

export const createTimeClockSchema = z
  .object({
    name: nonEmptyTrimmed(100, 'Name'),
    externalId: terminalId,
    locationId: uuidSchema,
  })
  .strict();
export type CreateTimeClockInput = z.infer<typeof createTimeClockSchema>;

export const updateTimeClockSchema = createTimeClockSchema.omit({ externalId: true }).partial().strict();
export type UpdateTimeClockInput = z.infer<typeof updateTimeClockSchema>;

export const revokeTimeClockSchema = z.object({ reason: nonEmptyTrimmed(500, 'Reason') }).strict();
export type RevokeTimeClockInput = z.infer<typeof revokeTimeClockSchema>;

export const listTimeClocksQuerySchema = paginationQuerySchema.extend({
  status: z.enum(TIME_CLOCK_STATUS_VALUES).optional(),
  locationId: uuidSchema.optional(),
});
export type ListTimeClocksQuery = z.infer<typeof listTimeClocksQuerySchema>;

/** Each punch is a clock event recorded at once: clients send small batches often. */
export const TIME_CLOCK_MAX_PUNCHES = 200;

/** A punch as a time clock logged it: who, where, when (the terminal's clock), and its log id. */
export const timeClockPunchSchema = z
  .object({
    terminal: terminalId,
    /** The punch's id in the terminal's log: the same punch sent again is recorded once. */
    punchId: z.string().trim().min(1).max(100),
    employeeNumber: z.string().trim().min(1).max(30),
    /** With an offset: terminals keep local time. */
    punchedAt: z.iso.datetime({ offset: true }),
    /** When the terminal knows it; otherwise the punch closes an open session or opens the one due. */
    direction: z.enum(['IN', 'OUT'] as const).optional(),
  })
  .strict();
export type TimeClockPunchInput = z.infer<typeof timeClockPunchSchema>;

export const timeClockPunchBatchSchema = z
  .object({ punches: z.array(timeClockPunchSchema).min(1).max(TIME_CLOCK_MAX_PUNCHES) })
  .strict()
  .superRefine((v, ctx) => {
    const seen = new Set<string>();
    v.punches.forEach((p, i) => {
      const key = `${p.terminal}|${p.punchId}`;
      if (seen.has(key)) ctx.addIssue({ code: 'custom', path: ['punches', i, 'punchId'], message: 'This punch appears twice in the batch' });
      seen.add(key);
    });
  });
export type TimeClockPunchBatchInput = z.infer<typeof timeClockPunchBatchSchema>;
